import React from 'react';
import { Box } from '@mui/material';
import { alpha, darken, keyframes, lighten } from '@mui/material/styles';

/**
 * Ituka, a brick in a hard hat: the face of a Candidate's dashboard banner.
 *
 * Drawn in code so it takes its colours from the selected theme (the brick from the danger colour, the hat from the warning colour) and
 * never from a hex written here; the operator's theme and dark mode apply.
 *
 * Motion is small and finite: three bobs when it appears, then still. Only `transform` changes, and under `prefers-reduced-motion`
 * nothing moves at all. It is decoration, so it is hidden from a screen reader unless given a `label`.
 */

const bob = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
`;

// `still` is for where it is the logo and not a greeting (the header): always at rest, never bobbing on each page that mounts it.
const Ituka = ({ size = 96, label, still = false, sx, ...rest }) => (
  <Box
    component="svg"
    {...rest}
    viewBox="0 0 120 120"
    width={size}
    height={size}
    role={label ? 'img' : undefined}
    aria-label={label || undefined}
    aria-hidden={label ? undefined : true}
    focusable="false"
    sx={(theme) => ({
      flexShrink: 0,
      '--brick': theme.palette.error.main,
      '--brick-dark': darken(theme.palette.error.main, 0.22),
      '--brick-light': lighten(theme.palette.error.main, 0.28),
      '--hat': theme.palette.warning.main,
      '--hat-dark': darken(theme.palette.warning.main, 0.25),
      '--hat-light': lighten(theme.palette.warning.main, 0.35),
      '--paper': theme.palette.common.white,
      '--ink': theme.palette.common.black,
      '--shadow': alpha(theme.palette.common.black, 0.14),
      '& .ituka-body': still ? undefined : { animation: `${bob} 2.4s ease-in-out 3` },
      '@media (prefers-reduced-motion: reduce)': { '& *': { animation: 'none !important' } },
      ...(typeof sx === 'function' ? sx(theme) : sx),
    })}
  >
    <ellipse cx="60" cy="113" rx="34" ry="5" fill="var(--shadow)" />
    <g className="ituka-body">
      {/* Arms at its sides, then feet, then the brick itself with its lit top edge. */}
      <rect x="13" y="72" width="13" height="30" rx="6.5" fill="var(--brick-dark)" />
      <rect x="94" y="72" width="13" height="30" rx="6.5" fill="var(--brick-dark)" />
      <rect x="38" y="100" width="16" height="9" rx="4.5" fill="var(--brick-dark)" />
      <rect x="66" y="100" width="16" height="9" rx="4.5" fill="var(--brick-dark)" />
      <rect x="24" y="44" width="72" height="60" rx="10" fill="var(--brick)" />
      <rect x="24" y="44" width="72" height="9" rx="4.5" fill="var(--brick-light)" opacity="0.55" />
      <circle cx="47" cy="70" r="7.5" fill="var(--paper)" />
      <circle cx="73" cy="70" r="7.5" fill="var(--paper)" />
      <circle cx="47" cy="70" r="3.6" fill="var(--ink)" />
      <circle cx="73" cy="70" r="3.6" fill="var(--ink)" />
      <path d="M49 85 Q60 95 71 85" fill="none" stroke="var(--ink)" strokeWidth="3.2" strokeLinecap="round" />
      {/* The hard hat: dome, ridge, brim. */}
      <path d="M31 44 A29 22 0 0 1 89 44 Z" fill="var(--hat)" />
      <rect x="55" y="21" width="10" height="23" rx="5" fill="var(--hat-light)" />
      <rect x="25" y="41" width="70" height="8" rx="4" fill="var(--hat-dark)" />
    </g>
  </Box>
);

export default Ituka;
